import { memo, useCallback, useRef } from "react"
import type { EnrichedPost } from "~convex/queryHelpers"
import { PostCard } from "./PostCard"
import { FeedListSkeleton } from "./Skeletons"
import { Spinner } from "./Spinner"

interface FeedListProps {
	emptyMessage?: string
	hasMore: boolean
	isLoading: boolean
	loadMore: () => void
	posts: EnrichedPost[]
}

export const FeedList = memo(function FeedList({
	emptyMessage = "No posts yet",
	hasMore,
	isLoading,
	loadMore,
	posts,
}: FeedListProps) {
	const stateRef = useRef({ hasMore, isLoading, loadMore })
	stateRef.current = { hasMore, isLoading, loadMore }

	const observerRef = useRef<IntersectionObserver | null>(null)
	const observeSentinel = useCallback((node: HTMLDivElement | null) => {
		if (observerRef.current) {
			observerRef.current.disconnect()
			observerRef.current = null
		}
		if (!node) {
			return
		}
		const observer = new IntersectionObserver(
			entries => {
				const { hasMore, isLoading, loadMore } = stateRef.current
				if (entries[0]?.isIntersecting && hasMore && !isLoading) {
					loadMore()
				}
			},
			{ rootMargin: "200px" },
		)
		observer.observe(node)
		observerRef.current = observer
	}, [])

	if (isLoading && posts.length === 0) {
		return <FeedListSkeleton />
	}

	if (posts.length === 0) {
		return <div className="py-12 text-center text-app-text-secondary">{emptyMessage}</div>
	}

	return (
		<div className="animate-fade-in">
			{posts.map(post => (
				<PostCard key={post._id} post={post} />
			))}
			<div className="h-1" ref={observeSentinel} />
			{isLoading && (
				<div className="flex justify-center py-4">
					<Spinner size="sm" />
				</div>
			)}
		</div>
	)
})
